import { ChevronDownMedium, ChevronUpMedium } from '@zoopla-eng/zds/icons';
import { Select } from '@zoopla-eng/zds/select';
import { fontGroups } from '@zoopla-eng/zds/themes';
import { useState } from 'react';
import { DropdownContainer } from '../DropdownContainer';
import {
    selectTravelToggleArrow,
    selectTravelToggleButton,
    selectTravelToggleLabel,
    selectTravelToggleText,
    selectTravelWrapperStyle
} from './SelectMaxTravelTimeAndTransportMode.styles.css';

const durations = {
    '900': '15 minutes',
    '1800': '30 minutes',
    '2700': '45 minutes',
    '3600': '1 hour',
    '4500': '1 ¼ hour'
};

const durationOptions = Object.keys(durations).map((duration, index) => ({
    id: duration,
    text: durations[duration as Duration]
}));
export type Duration = keyof typeof durations;

const transportTypes = {
    driving: 'Driving',
    walking: 'Walking',
    walking_train: 'Train',
    public_transport: 'Public transport',
    cycling: 'Cycling'
};

const transportTypeOptions = Object.keys(transportTypes).map(
    (transportType, index) => ({
        id: transportType,
        text: transportTypes[transportType as TransportType]
    })
);

export type TransportType = keyof typeof transportTypes;

const travelTimeLabelText = 'Max travel time';
const transportTypeLabelText = 'Transport type';

type SelectMaxTravelTimeAndTransportModeProps = {
    value: {
        duration: Duration;
        transportType: TransportType;
    };
    onChange: ({
        duration,
        transportType
    }: {
        duration: Duration;
        transportType: TransportType;
    }) => void;
};

export const SelectMaxTravelTimeAndTransportMode = ({
    value,
    onChange
}: SelectMaxTravelTimeAndTransportModeProps) => {
    const [isActive, setIsActive] = useState(false);

    const toggle = () => {
        setIsActive(!isActive);
    };

    return (
        <div
            className={selectTravelWrapperStyle}
            data-testid="sub_header_max-travel-and-transport-type-dropdowns"
            title={`${durations[value.duration]} • ${
                transportTypes[value.transportType]
            }`}
        >
            <button
                className={`${selectTravelToggleButton} ${fontGroups.ladle}`}
                onClick={toggle}
            >
                <label className={selectTravelToggleLabel}>Travel time</label>
                <span className={selectTravelToggleText}>
                    {durations[value.duration]} •{' '}
                    {transportTypes[value.transportType]}
                </span>
                <div className={selectTravelToggleArrow} aria-hidden>
                    {isActive ? <ChevronUpMedium /> : <ChevronDownMedium />}
                </div>
            </button>
            <DropdownContainer isActive={isActive}>
                <Select
                    options={durationOptions}
                    onSelectedOptionChange={(o) => {
                        onChange({
                            ...value,
                            // TODO: P5ZDS fix select to allow consumer to type Option
                            // (needs forwardRef type hack)
                            duration: o.id as Duration
                        });
                    }}
                    selectedOption={
                        durationOptions.find((o) => o.id === value.duration) ??
                        durationOptions[0]
                    }
                    label={travelTimeLabelText}
                    data-testid="sub_header_select-max-travel-time-dropdown"
                />
                <Select
                    options={transportTypeOptions}
                    onSelectedOptionChange={(o) => {
                        onChange({
                            ...value,
                            transportType: o.id as TransportType
                        });
                    }}
                    selectedOption={
                        transportTypeOptions.find(
                            (o) => o.id === value.transportType
                        ) ?? transportTypeOptions[0]
                    }
                    label={transportTypeLabelText}
                    data-testid="sub_header_select-transport-type-dropdown"
                />
            </DropdownContainer>
        </div>
    );
};
